import {
  CardContent,
  Grid,
  ListItem,
  ListItemText,
  Typography,
  useEventCallback,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Fragment, useState } from 'react';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PmiCard } from 'shell/components/shared/card';
import PmiTitle from 'shell/components/shared/title';
import {
  AuthenticAuthorizations,
  CashCheckAuthorization,
  CertificationAuthorizations,
  ClaimsAuthorizations,
  RegisterCertificationAuthorizations,
  TypeDocAuthorizations,
  StatementsAuthorizations,
  HistoricalAuthorizations,
  ResidencyPermitAuthorizations,
  ReleaseModeAuthorizations,
  PrintReceiptPosAuthorizations,
  RegistrationIDCardAuthorizations,
  ReleaseIdCardAuthorizations,
  CashManagementAuthorizations,
  LotsManagementAuthorizations,
  ReprintCidAuthorizations,
  RetrieveIdCardAuthorizations,
  AmountAuthorizations,
  LeverAuthorizations,
  ElectoralAuthorizations,
  ElectoralCardAuthorizations,
  AnnullamentIDCardAuthorizations,
  ElectoralCardTableAuthorizations,
  FrontOfficeAuthorizations,
} from 'shell/authorizations/front-office';
import CardMenu from './card-menu';
import TableServiceMenu from './table-service-menu';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonFrontOfficeCrumbs } from 'front-office/constants/breadcrumb';
import { ActCertified, ActPrint } from 'shell/authorizations/civil-status';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import CertifiedCivilUnionMenu from './certified-civil-union-menu';

const HomePage = (): React.ReactElement => {
  const actions = useActionAuthorization({
    certificazione: CertificationAuthorizations,
    attestazione: ClaimsAuthorizations,
    autentiche: AuthenticAuthorizations,
    dichiarazioni: StatementsAuthorizations,
    storiche: HistoricalAuthorizations,
    controlloDiCassa: CashCheckAuthorization,
    ricercaRegistro: RegisterCertificationAuthorizations,
    tipoDoc: TypeDocAuthorizations,
    permessoDiSoggiorno: ResidencyPermitAuthorizations,
    modalitaRilascio: ReleaseModeAuthorizations,
    stampaRicevutPos: PrintReceiptPosAuthorizations,
    registrazioneCarta: RegistrationIDCardAuthorizations,
    annullamentoCarta: AnnullamentIDCardAuthorizations,
    rilascioCarta: ReleaseIdCardAuthorizations,
    leva: LeverAuthorizations,
    elettorale: ElectoralAuthorizations,
    elettoraleAnpr: [FrontOfficeAuthorizations.ElettoraleAnpr],
    ufficioCassa: CashManagementAuthorizations,
    gestioneLotti: LotsManagementAuthorizations,
    ristampaCid: ReprintCidAuthorizations,
    recuperoCartelliniCie: RetrieveIdCardAuthorizations,
    ricercaImporto: AmountAuthorizations,
    tesseraElettorale: ElectoralCardAuthorizations,
    tabelleTessereElettorale: ElectoralCardTableAuthorizations,
    autorizzazioneVoto: [FrontOfficeAuthorizations.StampaAutorizzazioneVoto],
    actCertified: ActCertified,
    actPrint: ActPrint,
  });

  const { userProfile } = useUserProfile();

  const featureSIMA13 = userProfile.datiLogin?.profile?.feature?.sipoWeb;

  const [cardSubmenuAnchor, setCardSubmenuAnchor] = useState<null | HTMLElement>(null);

  const isCardSubmenuVisible = Boolean(cardSubmenuAnchor);

  const [civilUnionSubmenuAnchor, setCivilUnionSubmenuAnchor] = useState<null | HTMLElement>(null);

  const isCivilUnionSubmenuVisible = Boolean(civilUnionSubmenuAnchor);

  const [tableServiceSubmenuAnchor, setTableServiceSubmenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const isTableServiceSubmenuVisible = Boolean(tableServiceSubmenuAnchor);

  const handleCivilUnionMenuClick = useEventCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setCivilUnionSubmenuAnchor(event.currentTarget);
  });

  const handleCivilUnionSubmenuClose = useEventCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setCivilUnionSubmenuAnchor(null);
  });

  const handleCardMenuClick = useEventCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setCardSubmenuAnchor(event.currentTarget);
  });

  const handleCardSubmenuClose = useEventCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setCardSubmenuAnchor(null);
  });

  const handleTableServiceMenuClick = useEventCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setTableServiceSubmenuAnchor(event.currentTarget);
  });

  const handleTableServiceSubmenuClose = useEventCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setTableServiceSubmenuAnchor(null);
  });

  const gestioneTabelleSistema = userProfile.datiLogin?.profile?.feature?.sistema;

  return (
    <Fragment>
      <Breadcrumbs items={CommonFrontOfficeCrumbs} />
      <PmiTitle title="Sportello" paddingBottom={1} />
      <div className="pmi-mb-4">
        <Typography component="div" fontSize=".9rem" color="primary">
          {"Seleziona l'area su cui iniziare ad operare"}
        </Typography>
      </div>
      <Grid container spacing={2}>
        {actions.attestazione ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/attestazione" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Attestazioni" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.autentiche ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/autentiche" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Autentiche" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.autorizzazioneVoto ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/autorizzazione-voto" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Autorizzazione al voto" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {!actions.rilascioCarta &&
        !actions.ristampaCid &&
        !actions.registrazioneCarta &&
        !actions.gestioneLotti &&
        !actions.recuperoCartelliniCie ? undefined : (
          <Grid item xs={12} md={4}>
            <PmiCard onClick={handleCardMenuClick}>
              <CardContent>
                <CardMenu
                  visibility={{
                    rilascioCarta: actions.rilascioCarta,
                    ristampaCid: actions.ristampaCid,
                    registrazioneCarta: actions.registrazioneCarta,
                    annullamentoCarta: actions.annullamentoCarta,
                    gestioneLotti: actions.gestioneLotti,
                    recuperoCartelliniCie: actions.recuperoCartelliniCie,
                  }}
                  isSubmenuVisible={isCardSubmenuVisible}
                  onClick={handleCardMenuClick}
                  onClose={handleCardSubmenuClose}
                  submenuAnchor={cardSubmenuAnchor}
                />
              </CardContent>
            </PmiCard>
          </Grid>
        )}
        {actions.certificazione ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/certificazione" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Certificazione ANPR" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.elettorale ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/elettorale" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Certificazione Elettorale" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.elettoraleAnpr ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/elettorale-anpr" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Certificazione Elettorale ANPR" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.leva ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/leva" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Certificazione Leva" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {(actions.actCertified || actions.actPrint) &&
        (featureSIMA13 === '1' || featureSIMA13 === '3') ? (
          // <Grid item xs={12} md={4}>
          //   <Link to="/sportello/stato-civile/certificazione" className="pmi-anchor">
          //     <PmiCard>
          //       <CardContent>
          //         <ListItem>
          //           <ListItemText primary="Certificazione Stato Civile" />
          //         </ListItem>
          //       </CardContent>
          //     </PmiCard>
          //   </Link>
          // </Grid>
          <Grid item xs={12} md={4}>
            <PmiCard onClick={handleCivilUnionMenuClick}>
              <CardContent>
                <CertifiedCivilUnionMenu
                  visibility={{
                    actCertified: actions.actCertified,
                    actPrint: actions.actPrint,
                  }}
                  isSubmenuVisible={isCivilUnionSubmenuVisible}
                  onClick={handleCivilUnionMenuClick}
                  onClose={handleCivilUnionSubmenuClose}
                  submenuAnchor={civilUnionSubmenuAnchor}
                />
              </CardContent>
            </PmiCard>
          </Grid>
        ) : undefined}
        {actions.storiche ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/storiche" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Certificazione Storica" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.dichiarazioni ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/dichiarazioni" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Dichiarazioni sostitutive" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.ufficioCassa ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/gestione-cassa" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Gestione Cassa" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.controlloDiCassa ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/controllo-di-cassa" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Lista servizi rilasciati" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {actions.tabelleTessereElettorale ? (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/tessere-elettorali" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Lista Tessere elettorali" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        ) : undefined}
        {!actions.permessoDiSoggiorno ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/rilascioPDS" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Permesso di soggiorno" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {!actions.tesseraElettorale ? undefined : (
          <Grid item xs={12} md={4}>
            <Link to="/sportello/tessera-elettorale" className="pmi-anchor">
              <PmiCard>
                <CardContent>
                  <ListItem>
                    <ListItemText primary="Rilascio Tessera Elettorale" />
                  </ListItem>
                </CardContent>
              </PmiCard>
            </Link>
          </Grid>
        )}
        {(!actions.tipoDoc &&
          !actions.modalitaRilascio &&
          !actions.ricercaRegistro &&
          !actions.ricercaImporto) ||
        gestioneTabelleSistema === '0' ? undefined : (
          <Grid item xs={12} md={4}>
            <PmiCard onClick={handleTableServiceMenuClick}>
              <CardContent>
                <TableServiceMenu
                  visibility={{
                    tipoDoc: actions.tipoDoc,
                    modalitaRilascio: actions.modalitaRilascio,
                    ricercaRegistro: actions.ricercaRegistro,
                    ricercaImporto: actions.ricercaImporto,
                  }}
                  isSubmenuVisible={isTableServiceSubmenuVisible}
                  onClick={handleTableServiceMenuClick}
                  onClose={handleTableServiceSubmenuClose}
                  submenuAnchor={tableServiceSubmenuAnchor}
                />
              </CardContent>
            </PmiCard>
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

HomePage.displayName = 'home page component';

export default HomePage;
